import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {AccessibilityInfo, ActivityIndicator, Pressable, ScrollView, TextInput, useWindowDimensions, View} from 'react-native';
import {Chess} from 'chess.js';
import ChessboardComponent from 'chessboard-package';
import Ionicons from 'react-native-vector-icons/Ionicons';

import CCText from '../../../Atoms/CCText';
import {errorText, useCourseReview, useReviewActions} from '../../../Hooks/LmsCourseApi/useCourseReview';

import CourseArtwork from './CourseArtwork';
import CourseButton from './CourseButton';
import {chapterStatus, linesMatch, nextToReview, ReviewFlag, ReviewPage, Signal} from '../reviewLogic';
import {CARD_SHADOW, F, S, STATUS, T} from '../courseTheme';

type Props = {bookUuid: string; onBack: () => void};

const tone = (level: string) => STATUS[level as keyof typeof STATUS] ?? STATUS.unknown;

/** Web transitions, kept off the RN style types and out of reduced-motion sessions. */
const MOTION = {transitionDuration: '160ms', transitionProperty: 'background-color, border-color, opacity'} as any;

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    let alive = true;
    // Guard the *result*, not just the method: the RN jest mock returns
    // undefined, and `?.()` still hands you an undefined to call .then on.
    Promise.resolve(AccessibilityInfo.isReduceMotionEnabled?.())
      .then(v => { if (alive) setReduced(!!v); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return reduced;
}

/**
 * Status as icon + colour + word.
 *
 * The screen used to encode every verdict in a bare coloured dot, which is
 * unreadable to a red/green colour-blind reviewer — and this screen exists to
 * be read carefully. The glyph carries the meaning; the colour reinforces it.
 */
function StatusIcon({level, muted, size = 16}: {level: string; muted?: boolean; size?: number}) {
  const t = tone(level);
  return <Ionicons name={muted ? 'ellipse-outline' : (t.icon as string)} size={size} color={t.fg} />;
}

function SectionLabel({children, first}: {children: string; first?: boolean}) {
  return (
    <CCText
      accessibilityRole="header"
      style={{
        fontSize: T.xs, fontWeight: '700', letterSpacing: 0.7, color: F.mutedText,
        marginTop: first ? 0 : S[5], paddingTop: first ? 0 : S[4],
        borderTopWidth: first ? 0 : 1, borderTopColor: F.border10, marginBottom: S[2],
      }}>
      {children}
    </CCText>
  );
}

function SignalRow({s, locked}: {s: Signal; locked?: boolean}) {
  const t = tone(s[0]);
  return (
    <View style={{flexDirection: 'row', gap: S[3], paddingVertical: S[2]}}>
      <View style={{marginTop: 1}}><StatusIcon level={s[0]} /></View>
      <View style={{flex: 1}}>
        <CCText style={{fontSize: T.sm, fontWeight: '600', color: s[0] === 'good' ? F.brownDark : t.fg, lineHeight: 19}}>
          {s[1]}{locked && s[0] === 'bad' ? '  — locked, cannot be dismissed' : ''}
        </CCText>
        {s[2] ? <CCText style={{fontSize: T.xs, color: F.brown, lineHeight: 18, marginTop: 1}}>{s[2]}</CCText> : null}
      </View>
    </View>
  );
}

/** How much of the course the reviewer has actually looked at. */
function Meter({done, total}: {done: number; total: number}) {
  const pct = total ? Math.min(1, done / total) : 0;
  return (
    <View style={{flexDirection: 'row', alignItems: 'center', gap: S[2]}}>
      <View style={{width: 84, height: 6, borderRadius: 3, backgroundColor: '#E8E6E4', overflow: 'hidden'}}>
        <View style={{width: `${pct * 100}%`, height: 6, borderRadius: 3, backgroundColor: pct === 1 ? STATUS.good.fg : F.teal}} />
      </View>
      <CCText style={{fontSize: T.xs, color: F.mutedText, fontVariant: ['tabular-nums']}}>{`${done}/${total} opened`}</CCText>
    </View>
  );
}

/**
 * Review a generated course before it is published.
 *
 * Three panes: the chapter rail as a heat map, the page under review with
 * the coach's words beside the position, and the signals with the fixes.
 * The reviewer does not find the problems; the pipeline's own provenance
 * points at them, and a real move validator sits under everything.
 */
/** How often, and how many times, to look for new artwork after asking for it. */
const ART_POLL_MS = 8000;
const ART_POLL_TRIES = 15;

export default function ReviewScreen({bookUuid, onBack}: Props) {
  // "Opened" is a fact about a person having looked, and the publish gate
  // demands it for every puzzle — 27 of them on the Rotlewi course. Holding it
  // only in component state meant a refresh threw the whole afternoon away, so
  // it is kept per course. It is a reviewer's own record, hence local.
  const storeKey = `ccReviewOpened:${bookUuid}`;
  const [opened, setOpened] = useState<Set<number>>(() => {
    try {
      const raw = globalThis.localStorage?.getItem(storeKey);
      const ids = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(ids) ? ids.filter((n: unknown) => typeof n === 'number') : []);
    } catch {
      return new Set();
    }
  });
  useEffect(() => {
    try {
      globalThis.localStorage?.setItem(storeKey, JSON.stringify(Array.from(opened)));
    } catch {
      /* private mode, quota, or no DOM — the gate just asks again */
    }
  }, [opened, storeKey]);
  const openedList = useMemo(() => Array.from(opened).sort((a, b) => a - b), [opened]);
  const review = useCourseReview(bookUuid, openedList);
  const actions = useReviewActions(bookUuid);
  const {width} = useWindowDimensions();
  const narrow = width < 1150;
  const reduced = useReducedMotion();
  const motion = reduced ? null : MOTION;

  const pages: ReviewPage[] = useMemo(
    () => (review.data?.chapters ?? []).flatMap(c => c.pages),
    [review.data],
  );
  const flags: ReviewFlag[] = review.data?.flags ?? [];

  const [currentId, setCurrentId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dismissing, setDismissing] = useState<ReviewFlag | null>(null);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [titleDraft, setTitleDraft] = useState<string | null>(null);
  const [editingCourse, setEditingCourse] = useState(false);
  // The artwork being replaced, while a new one is being made in the
  // background; undefined when nothing is pending.
  const [awaitingArt, setAwaitingArt] = useState<string | null | undefined>(undefined);
  const [courseTitle, setCourseTitle] = useState('');
  const [courseDescription, setCourseDescription] = useState('');
  const [centreW, setCentreW] = useState(0);
  const [focusedId, setFocusedId] = useState<number | null>(null);
  const [play, setPlay] = useState<{game: any; selected: string | null; played: string[]} | null>(null);
  const [playStatus, setPlayStatus] = useState<{kind: 'ok' | 'off' | 'bad' | 'idle'; text: string}>({kind: 'idle', text: ''});

  // Open on the first page that needs a look, not on page 1. Review starts
  // where the pipeline was least sure.
  useEffect(() => {
    if (currentId != null || !pages.length) return;
    const first = pages.find(p => ['bad', 'warn'].includes(chapterStatus(p, flags).level)) ?? pages[0];
    setCurrentId(first.id);
    setOpened(s => new Set(s).add(first.id));
  }, [pages, flags, currentId]);

  // New artwork is made by a background task, so the payload is re-read until
  // the image changes (or a couple of minutes pass) instead of once.
  const reviewRefetch = review.refetch;
  const currentArt = review.data?.book?.thumbnail ?? null;
  useEffect(() => {
    if (awaitingArt === undefined) return undefined;
    if (currentArt !== awaitingArt) {
      setAwaitingArt(undefined);
      return undefined;
    }
    let tries = 0;
    const id = setInterval(() => {
      tries += 1;
      if (tries > ART_POLL_TRIES) {
        clearInterval(id);
        setAwaitingArt(undefined);
        return;
      }
      reviewRefetch?.();
    }, ART_POLL_MS);
    return () => clearInterval(id);
  }, [awaitingArt, currentArt, reviewRefetch]);

  const current = pages.find(p => p.id === currentId) ?? null;
  const block = current?.config?.review;

  const openPage = useCallback((id: number) => {
    setCurrentId(id);
    setOpened(s => new Set(s).add(id));
    setPlay(null);
    setPlayStatus({kind: 'idle', text: ''});
    setError(null);
    setTitleDraft(null);
  }, []);

  const runFix = useCallback((kind: string, extra: Record<string, unknown> = {}) => {
    if (!current) return;
    setError(null);
    actions.fix.mutate({pageId: current.id, kind, ...extra}, {
      onError: (e: unknown) => setError(errorText(e, 'That fix could not be applied.')),
      onSuccess: () => { setPlay(null); setPlayStatus({kind: 'idle', text: ''}); },
    });
  }, [current, actions.fix]);

  // ---- play mode ------------------------------------------------------
  const startPlay = () => {
    if (!current) return;
    setPlay({game: new Chess(current.content), selected: null, played: []});
    setPlayStatus({kind: 'idle', text: 'Click a piece, then a square.'});
  };

  const onSquareClick = (square: string) => {
    if (!play || !block) return;
    const game = play.game;
    if (!play.selected) {
      const piece = game.get(square);
      if (!piece || piece.color !== game.turn()) return;
      setPlay({...play, selected: square});
      return;
    }
    let move: any = null;
    try {
      move = game.move({from: play.selected, to: square, promotion: 'q'});
    } catch {
      move = null;
    }
    if (!move) {
      setPlay({...play, selected: null});
      setPlayStatus({kind: 'bad', text: 'Not a legal move.'});
      return;
    }
    const played = [...play.played, move.san];
    setPlay({game, selected: null, played});
    const verdict = linesMatch(played, block.lines);
    if (verdict === 'complete') setPlayStatus({kind: 'ok', text: `${played.join(' ')} — matches an accepted solution.`});
    else if (verdict === 'prefix') setPlayStatus({kind: 'ok', text: `${played.join(' ')} — on an accepted line so far.`});
    else setPlayStatus({kind: 'off', text: `${played.join(' ')} — legal, but not an accepted answer.`});
  };

  // ---- render -----------------------------------------------------------
  if (review.isLoading || !review.data) {
    return <View style={{flex: 1, backgroundColor: F.teal, alignItems: 'center', justifyContent: 'center'}}><ActivityIndicator color={F.white} /></View>;
  }

  const gate = review.data.gate;
  const published = review.data.review_state === 'published';
  const openCount = flags.filter(f => f.status === 'open').length;
  const failing = gate.checks.filter(c => !c.ok);
  const upNext = nextToReview(pages, flags, currentId ?? -1, opened);

  // The rail scrolls. It is a plain column of every page, and the Rotlewi
  // course has 55 of them — they ran off the bottom with no way to reach them.
  const rail = (
    <View style={{width: narrow ? undefined : 272, backgroundColor: F.white, borderRightWidth: narrow ? 0 : 1, borderRightColor: F.border10, maxHeight: narrow ? 220 : undefined}}>
      <View style={{paddingHorizontal: S[4], paddingTop: S[4], paddingBottom: S[2], flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'}}>
        <CCText accessibilityRole="header" style={{fontSize: T.xs, fontWeight: '700', letterSpacing: 0.7, color: F.mutedText}}>CHAPTERS</CCText>
        <CCText style={{fontSize: T.xs, color: F.mutedText, fontVariant: ['tabular-nums']}}>{`${pages.length}`}</CCText>
      </View>
      <ScrollView style={{flex: narrow ? undefined : 1}} contentContainerStyle={{paddingBottom: S[2]}}>
        {pages.map(p => {
          const s = chapterStatus(p, flags);
          const isCurrent = p.id === currentId;
          const seen = opened.has(p.id);
          const focused = focusedId === p.id;
          return (
            <Pressable
              key={p.id}
              accessibilityRole="button"
              accessibilityState={{selected: isCurrent}}
              accessibilityLabel={`Open ${p.heading ?? 'page'} ${p.id}`}
              onPress={() => openPage(p.id)}
              onFocus={() => setFocusedId(p.id)}
              onBlur={() => setFocusedId(null)}
              style={({pressed}) => [{
                flexDirection: 'row', gap: S[3], alignItems: 'center',
                paddingHorizontal: S[4], paddingVertical: S[3], minHeight: 48,
                backgroundColor: isCurrent ? 'rgba(61,171,158,0.12)' : pressed ? 'rgba(38,35,34,0.06)' : 'transparent',
                borderLeftWidth: 3, borderLeftColor: isCurrent ? F.teal : 'transparent',
                outlineWidth: focused ? 2 : 0, outlineColor: F.teal, outlineStyle: 'solid', outlineOffset: -2,
                cursor: 'pointer',
              } as any, motion]}>
              <StatusIcon level={s.level} muted={s.level === 'good' && !seen} />
              <View style={{flex: 1}}>
                <CCText numberOfLines={2} style={{fontSize: T.sm, fontWeight: isCurrent ? '700' : '600', color: F.brownDark, lineHeight: 18}}>{p.heading || `Page ${p.id}`}</CCText>
                <CCText style={{fontSize: T.xs, color: s.level === 'bad' || s.level === 'warn' ? tone(s.level).fg : F.mutedText, marginTop: 1}}>
                  {s.label}{s.level === 'good' && !seen ? ' · not yet opened' : ''}
                </CCText>
              </View>
              <View style={{backgroundColor: F.badgeBg, borderRadius: 4, paddingHorizontal: 5, paddingVertical: 2}}>
                <CCText style={{fontSize: 10, fontWeight: '700', color: F.mutedText, letterSpacing: 0.4}}>{p.content_type === 'video' ? 'VIDEO' : 'PUZZLE'}</CCText>
              </View>
            </Pressable>
          );
        })}
        <View style={{margin: S[4], padding: S[3], borderWidth: 1, borderStyle: 'dashed', borderColor: 'rgba(38,35,34,0.2)', borderRadius: 10}}>
          <CCText style={{fontSize: T.xs, fontWeight: '700', letterSpacing: 0.7, color: F.mutedText}}>{`DISCARDED BEFORE REVIEW (${review.data.discarded.length})`}</CCText>
          {review.data.discarded.slice(0, 6).map((d, i) => (
            <CCText key={i} style={{fontSize: T.xs, color: F.brown, marginTop: S[1], lineHeight: 17}}>{`${d.time != null ? Math.round(d.time) + 's · ' : ''}${d.stage ? d.stage.replace(/_/g, ' ') + ' · ' : ''}${d.reasons[0]}`}</CCText>
          ))}
          <CCText style={{fontSize: T.xs, color: F.mutedText, marginTop: S[2]}}>{`${review.data.summary.puzzles + review.data.discarded.length} candidates · ${review.data.summary.puzzles} kept · ${review.data.discarded.length} discarded`}</CCText>
        </View>
      </ScrollView>
    </View>
  );

  // The centre pane sizes itself from the space the rail and signals pane
  // leave over, which the window width does not predict. Measure it.
  const paneW = centreW || (narrow ? width : width - 640);
  const twoUp = paneW >= 700;
  const BOARD_PANE = 348;
  const boardSize = Math.max(240, Math.min(300, (twoUp ? BOARD_PANE : paneW) - 64));

  const verdict = !block ? null : block.hard ? 'bad' : block.signals.some((s: Signal) => s[0] === 'bad') ? 'bad'
    : block.signals.some((s: Signal) => s[0] === 'warn') ? 'warn' : 'good';

  // The board must NOT sit inside a ScrollView: the scroll responder claims the
  // touch and dragging a piece stops working. This pane is a plain View, and
  // only the evidence column beside the board scrolls.
  const centre = current ? (
    <View
      style={{flex: 1, padding: S[5], gap: S[4]}}
      onLayout={e => setCentreW(e.nativeEvent.layout.width)}>

      <View style={{gap: S[2]}}>
        <CCText accessibilityRole="header" style={{fontSize: T.xl, fontWeight: '700', color: F.brownDark, lineHeight: 27}}>{current.heading || `Page ${current.id}`}</CCText>
        {verdict ? (
          <View style={{flexDirection: 'row', alignItems: 'center', gap: S[2], alignSelf: 'flex-start', backgroundColor: tone(verdict).bg, borderRadius: 999, paddingHorizontal: S[3], paddingVertical: 5}}>
            <StatusIcon level={verdict} size={14} />
            <CCText style={{fontSize: T.xs, fontWeight: '700', color: tone(verdict).fg}}>
              {verdict === 'bad' ? 'Needs a decision' : verdict === 'warn' ? 'Worth a look' : 'Nothing flagged'}
            </CCText>
          </View>
        ) : null}
      </View>

      {block?.hard ? (
        <View accessibilityRole="alert" style={{backgroundColor: STATUS.bad.bg, borderLeftWidth: 4, borderLeftColor: STATUS.bad.fg, borderRadius: 10, padding: S[4], flexDirection: 'row', gap: S[3]}}>
          <Ionicons name="alert-circle" size={18} color={STATUS.bad.fg} />
          <View style={{flex: 1}}>
            <CCText style={{fontSize: T.sm, fontWeight: '700', color: F.brownDark}}>Fails a machine check — cannot be dismissed</CCText>
            <CCText style={{fontSize: T.sm, color: F.brown, marginTop: 2, lineHeight: 19}}>{block.machine.filter((m: Signal) => m[0] === 'bad').map((m: Signal) => m[1]).join(' · ')}</CCText>
          </View>
        </View>
      ) : null}

      {current.content_type === 'chess_position' && block ? (
        // Side-by-side only when THIS pane is actually wide enough. Keying off
        // the window made the solutions card collapse to a one-character
        // column whenever the rail and signals pane ate the width.
        <View style={{flex: 1, flexDirection: twoUp ? 'row' : 'column', gap: S[4], alignItems: 'stretch'}}>
          <View style={[{backgroundColor: F.white, borderRadius: 14, padding: S[4], width: twoUp ? BOARD_PANE : '100%', alignSelf: 'flex-start'}, CARD_SHADOW]}>
            <ChessboardComponent
              position={play ? play.game.fen() : current.content}
              boardOrientation={(current.config.view ?? current.content.split(' ')[1]) === 'b' ? 'black' : 'white'}
              boardWidth={boardSize}
              disabled={!play}
              onSquareClick={onSquareClick}
              handleNewFen={() => {}}
            />
            <View style={{flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: S[3], gap: S[2]}}>
              <View style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
                <View style={{width: 12, height: 12, borderRadius: 6, borderWidth: 1, borderColor: F.mutedText, backgroundColor: current.content.split(' ')[1] === 'b' ? F.brownDark : F.white}} />
                <CCText style={{fontSize: T.sm, fontWeight: '700', color: F.brownDark}}>{current.content.split(' ')[1] === 'b' ? 'Black to move' : 'White to move'}</CCText>
              </View>
              <CourseButton label={play ? 'Stop playing' : 'Play the puzzle'} variant="review" onPress={play ? () => setPlay(null) : startPlay} />
            </View>
            {playStatus.text ? (
              <View
                accessibilityLiveRegion="polite"
                style={{marginTop: S[3], padding: S[3], borderRadius: 8, flexDirection: 'row', gap: S[2], alignItems: 'flex-start',
                  backgroundColor: playStatus.kind === 'bad' ? STATUS.bad.bg : playStatus.kind === 'off' ? STATUS.warn.bg : playStatus.kind === 'ok' ? STATUS.good.bg : F.badgeBg}}>
                {playStatus.kind !== 'idle' ? <StatusIcon level={playStatus.kind === 'ok' ? 'good' : playStatus.kind === 'off' ? 'warn' : 'bad'} size={15} /> : null}
                <CCText style={{flex: 1, fontSize: T.sm, fontWeight: '600', lineHeight: 19,
                  color: playStatus.kind === 'bad' ? STATUS.bad.fg : playStatus.kind === 'off' ? STATUS.warn.fg : playStatus.kind === 'ok' ? STATUS.good.fg : F.brown}}>
                  {playStatus.text}
                </CCText>
              </View>
            ) : null}
            {playStatus.kind === 'off' && play ? (
              <View style={{alignSelf: 'flex-start', marginTop: S[2]}}>
                <CourseButton label="Accept this line too" variant="review" onPress={() => runFix('accept_line', {san: play.played})} />
              </View>
            ) : null}
            <CCText selectable style={{fontSize: T.xs, color: F.mutedText, marginTop: S[3], lineHeight: 16}}>{current.content}</CCText>
          </View>

          <ScrollView
            style={{flex: 1, minWidth: twoUp ? 320 : undefined, width: twoUp ? undefined : '100%'}}
            contentContainerStyle={{gap: S[4], paddingBottom: S[2]}}>
            <View style={[{backgroundColor: F.white, borderRadius: 14, padding: S[4]}, CARD_SHADOW]}>
              <SectionLabel first>{`ACCEPTED SOLUTIONS · GOAL: ${(block.goal || 'win').toUpperCase()}`}</SectionLabel>
              {block.lines.map((l: any, i: number) => (
                <View key={i} style={{flexDirection: 'row', alignItems: 'center', gap: S[3], paddingVertical: 5, borderTopWidth: i ? 1 : 0, borderTopColor: F.border10}}>
                  <CCText style={{fontSize: T.base, color: F.brownDark, flex: 1, lineHeight: 21}}>{l.san.join(' ')}</CCText>
                  <View style={{backgroundColor: l.tag === 'coach' ? 'rgba(61,171,158,0.14)' : F.badgeBg, borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2}}>
                    <CCText style={{fontSize: 10, fontWeight: '700', letterSpacing: 0.3, color: l.tag === 'coach' ? F.tealDark : F.mutedText}}>{l.tag === 'alt' ? 'ALSO ACCEPTED' : String(l.tag).toUpperCase()}</CCText>
                  </View>
                </View>
              ))}
              {block.coach_move && block.lines[0]?.tag !== 'coach' ? (
                <CCText style={{fontSize: T.xs, color: STATUS.warn.fg, marginTop: S[2], lineHeight: 17}}>{`Coach played ${block.coach_move} on screen. It is not currently the accepted answer.`}</CCText>
              ) : null}
            </View>

            <View style={[{backgroundColor: F.white, borderRadius: 14, padding: S[4]}, CARD_SHADOW]}>
              <SectionLabel first>HINT</SectionLabel>
              <CCText style={{fontSize: T.base, color: current.config.hint ? F.brownDark : F.mutedText, lineHeight: 22}}>{current.config.hint || 'No hint yet — write one under “Or say what’s wrong”.'}</CCText>
            </View>

            <View style={[{backgroundColor: F.white, borderRadius: 14, padding: S[4]}, CARD_SHADOW]}>
              <SectionLabel first>WHAT THE COACH SAID — THE EVIDENCE</SectionLabel>
              {block.themes?.length ? (
                <View style={{flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: S[3]}}>
                  {block.themes.map(t => (
                    <View key={t} style={{backgroundColor: F.badgeBg, borderRadius: 999, paddingHorizontal: S[2], paddingVertical: 3}}>
                      <CCText style={{fontSize: 10, fontWeight: '700', letterSpacing: 0.3, color: F.brown}}>{t.toUpperCase()}</CCText>
                    </View>
                  ))}
                </View>
              ) : null}
              {/* The split is the point: the set-up is what a hint may be
                  written from, the rest contains the answer. */}
              {block.lead_in ? (
                <View style={{marginBottom: S[3]}}>
                  <CCText style={{fontSize: 10, fontWeight: '700', letterSpacing: 0.4, color: F.tealDark, marginBottom: 3}}>
                    HOW IT WAS SET UP — BEFORE THE BOARD WENT UP
                  </CCText>
                  <CCText style={{fontSize: T.base, color: F.brownDark, lineHeight: 23}}>{block.lead_in}</CCText>
                </View>
              ) : null}
              {block.transcript || block.lead_in ? (
                <View>
                  {block.lead_in ? (
                    <CCText style={{fontSize: 10, fontWeight: '700', letterSpacing: 0.4, color: F.mutedText, marginBottom: 3}}>
                      THE WHOLE DISCUSSION
                    </CCText>
                  ) : null}
                  <CCText style={{fontSize: T.base, color: F.brownDark, lineHeight: 23}}>{block.transcript}</CCText>
                </View>
              ) : (
                <CCText style={{fontSize: T.base, color: F.mutedText, lineHeight: 23}}>(nothing said in this window)</CCText>
              )}
              {block.spoken_goal ? (
                <CCText style={{fontSize: T.xs, color: F.mutedText, marginTop: S[3], lineHeight: 17}}>
                  {`The coach stated the task as: ${block.spoken_goal}.`}
                </CCText>
              ) : null}
            </View>
          </ScrollView>
        </View>
      ) : (
        <View style={[{backgroundColor: F.white, borderRadius: 14, padding: S[4]}, CARD_SHADOW]}>
          <CCText style={{fontSize: T.base, color: F.brownDark, lineHeight: 22}}>{current.text || 'Video chapter.'}</CCText>
        </View>
      )}

      {error ? (
        <View accessibilityRole="alert" accessibilityLiveRegion="polite" style={{flexDirection: 'row', gap: S[2], alignItems: 'center', backgroundColor: STATUS.bad.bg, borderRadius: 10, padding: S[3]}}>
          <Ionicons name="alert-circle" size={16} color={STATUS.bad.fg} />
          <CCText style={{flex: 1, fontSize: T.sm, fontWeight: '600', color: STATUS.bad.fg}}>{error}</CCText>
        </View>
      ) : null}
    </View>
  ) : null;

  const pageFlags = current ? flags.filter(f => f.page_id === current.id) : [];
  const openHere = pageFlags.filter(f => f.status === 'open');

  const right = current ? (
    // flexGrow/Shrink 0 is load-bearing: ScrollView ships flex-grow:1, which
    // turns `width` into a mere starting basis — the pane then absorbed half
    // the free space (360 -> 745) and starved the board pane to 385.
    <ScrollView
      style={{width: narrow ? undefined : 368, flexGrow: 0, flexShrink: 0, backgroundColor: F.white, borderLeftWidth: narrow ? 0 : 1, borderLeftColor: F.border10}}
      contentContainerStyle={{padding: S[5]}}>
      {block ? (
        <>
          <SectionLabel first>MACHINE CHECKS</SectionLabel>
          {block.machine.map((s: Signal, i: number) => <SignalRow key={`m${i}`} s={s} locked />)}
          <SectionLabel>WHY IT'S FLAGGED</SectionLabel>
          {block.signals.length
            ? block.signals.map((s: Signal, i: number) => <SignalRow key={`s${i}`} s={s} />)
            : <CCText style={{fontSize: T.sm, color: F.mutedText, lineHeight: 19}}>Nothing flagged on this page. It still needs opening before publish.</CCText>}
        </>
      ) : null}

      {/* A video chapter has no move and no side, so it has no fixes — the
          header used to render anyway, over an empty rule. */}
      {current.content_type === 'chess_position' ? (
        <>
          <SectionLabel>FIX IT</SectionLabel>
          <View style={{gap: S[2]}}>
            <CourseButton label={`Side to move → ${current.content.split(' ')[1] === 'b' ? 'White' : 'Black'}`} variant="review" onPress={() => runFix('side')} />
            {block?.coach_move && block.lines[0]?.tag !== 'coach' ? (
              <CourseButton label={`Make the coach's move the answer (${block.coach_move})`} onPress={() => runFix('coach_move')} />
            ) : null}
          </View>
        </>
      ) : null}

      <SectionLabel>TITLE</SectionLabel>
      {current.config?.title_source === 'template' && !current.config?.review?.title_accepted ? (
        <CCText style={{fontSize: T.xs, color: STATUS.warn.fg, marginBottom: S[2], lineHeight: 17}}>
          This title was built from the board because the AI could not name it. Rename it, or keep it — publishing waits until you decide.
        </CCText>
      ) : null}
      <TextInput
        value={titleDraft ?? current.heading ?? ''}
        onChangeText={setTitleDraft}
        accessibilityLabel="Chapter title"
        placeholderTextColor={F.mutedText}
        style={{borderWidth: 1, borderColor: 'rgba(38,35,34,0.2)', borderRadius: 8, padding: S[3], fontSize: T.sm, color: F.brownDark}}
      />
      <View style={{flexDirection: 'row', gap: S[2], flexWrap: 'wrap', marginTop: S[2]}}>
        <CourseButton
          label="Rename chapter"
          variant="review"
          disabled={!titleDraft?.trim() || titleDraft.trim() === (current.heading ?? '')}
          onPress={() => { runFix('title', {text: (titleDraft ?? '').trim()}); setTitleDraft(null); }}
        />
        {current.config?.title_source === 'template' && !current.config?.review?.title_accepted ? (
          <CourseButton label="Keep this title" variant="review" onPress={() => runFix('title', {accept: true})} />
        ) : null}
      </View>

      <SectionLabel>OR SAY WHAT'S WRONG</SectionLabel>
      <CCText nativeID="noteHelp" style={{fontSize: T.xs, color: F.mutedText, marginBottom: S[2], lineHeight: 17}}>
        Flag it for someone else, or save the text as this puzzle's hint.
      </CCText>
      <TextInput
        value={note}
        onChangeText={setNote}
        accessibilityLabel="Note"
        accessibilityHint="Flag this page or save the text as the hint"
        placeholder="hint gives too much away · coach means the other rook"
        placeholderTextColor={F.mutedText}
        style={{borderWidth: 1, borderColor: 'rgba(38,35,34,0.2)', borderRadius: 8, padding: S[3], fontSize: T.sm, minHeight: 64, color: F.brownDark}}
        multiline
      />
      <View style={{flexDirection: 'row', gap: S[2], flexWrap: 'wrap', marginTop: S[3]}}>
        <CourseButton label="Flag this page" variant="review" disabled={!note.trim()} onPress={() => { actions.flag.mutate({pageId: current.id, kind: 'other', note: note.trim()}); setNote(''); }} />
        {current.content_type === 'chess_position' ? (
          <CourseButton label="Save as hint" variant="review" disabled={!note.trim()} onPress={() => { runFix('hint', {text: note.trim()}); setNote(''); }} />
        ) : null}
        {openHere.length && !block?.hard ? (
          <CourseButton label="Mark as fine…" variant="review" onPress={() => { setDismissing(openHere[0]); setReason(''); }} />
        ) : null}
      </View>
      {block?.hard ? (
        <View style={{flexDirection: 'row', gap: 6, alignItems: 'center', marginTop: S[2]}}>
          <Ionicons name="lock-closed" size={13} color={STATUS.bad.fg} />
          <CCText style={{fontSize: T.xs, color: STATUS.bad.fg}}>Cannot be marked fine — a machine check fails.</CCText>
        </View>
      ) : null}

      {current.content_type === 'chess_position' ? (
        <>
          <SectionLabel>REMOVE THE PUZZLE</SectionLabel>
          <CCText style={{fontSize: T.xs, color: F.mutedText, marginBottom: S[2], lineHeight: 17}}>
            Keeps the chapter and its video, and stops asking the student to solve anything here.
          </CCText>
          <View style={{borderWidth: 1, borderColor: 'rgba(192,57,43,0.35)', borderRadius: 10, padding: S[2]}}>
            <CourseButton label="Not a puzzle — keep as video" variant="review" onPress={() => runFix('drop')} />
          </View>
        </>
      ) : null}

      <CCText style={{fontSize: T.xs, color: F.mutedText, marginTop: S[6], lineHeight: 17}}>
        Prose is rewritten by the model, constrained to this window of the transcript. Moves and sides are never generated — they are chosen from what the coach played or what the engine found, and replayed on a real board before they are saved.
      </CCText>
    </ScrollView>
  ) : null;

  return (
    <View style={{flex: 1, backgroundColor: F.teal}}>
      <View style={{flexDirection: 'row', alignItems: 'center', gap: S[3], paddingHorizontal: S[4], paddingVertical: S[2], backgroundColor: F.white, borderBottomWidth: 1, borderBottomColor: F.border10, flexWrap: 'wrap'}}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Back to courses"
          onPress={onBack}
          hitSlop={12}
          style={({pressed}) => [{width: 44, height: 44, alignItems: 'center', justifyContent: 'center', borderRadius: 22, backgroundColor: pressed ? 'rgba(38,35,34,0.08)' : 'transparent', cursor: 'pointer'} as any, motion]}>
          <Ionicons name="arrow-back" size={20} color={F.brownDark} />
        </Pressable>
        <View>
          <CCText accessibilityRole="header" style={{fontSize: T.lg, fontWeight: '700', color: F.brownDark}}>{review.data.book.title}</CCText>
          <View style={{flexDirection: 'row', alignItems: 'center', gap: S[2], marginTop: 1}}>
            <CCText style={{fontSize: T.xs, fontWeight: '700', letterSpacing: 0.5, color: published ? STATUS.good.fg : STATUS.warn.fg}}>
              {published ? 'PUBLISHED' : review.data.review_state === 'in_review' ? 'IN REVIEW' : 'DRAFT'}
            </CCText>
            {openCount ? (
              <View style={{flexDirection: 'row', alignItems: 'center', gap: 4}}>
                <Ionicons name="flag" size={11} color={STATUS.warn.fg} />
                <CCText style={{fontSize: T.xs, color: STATUS.warn.fg}}>{`${openCount} open flag${openCount === 1 ? '' : 's'}`}</CCText>
              </View>
            ) : null}
          </View>
        </View>
        <View style={{flex: 1, minWidth: S[4]}} />
        {review.data.book.thumbnail ? (
          <View style={{width: 96}}>
            <CourseArtwork title={review.data.book.title} thumbnail={review.data.book.thumbnail} showTitle={false} radius={6} />
          </View>
        ) : null}
        <CourseButton
          label={awaitingArt !== undefined ? 'Making new artwork…' : 'Regenerate artwork'}
          variant="review"
          disabled={!!actions.regenerateThumbnail?.isLoading || awaitingArt !== undefined}
          onPress={() => actions.regenerateThumbnail?.mutate(undefined, {
            onSuccess: () => setAwaitingArt(review.data?.book?.thumbnail ?? null),
            onError: (e: unknown) => setError(errorText(e, 'Could not queue the artwork.')),
          })}
        />
        <CourseButton
          label="Edit course"
          variant="review"
          onPress={() => {
            setCourseTitle(review.data!.book.title);
            setCourseDescription(review.data!.book.description ?? '');
            setEditingCourse(open => !open);
          }}
        />
        <Meter done={openedList.length} total={review.data.summary.puzzles} />
        <CourseButton
          label={upNext ? (upNext.why === 'flagged' ? 'Next flagged →' : 'Next unopened →') : 'All reviewed'}
          variant="review"
          disabled={!upNext}
          onPress={() => { if (upNext) openPage(upNext.id); }}
        />
        {published ? (
          // Publishing was a one-way door: the course locked and the only
          // control left said "Published". The endpoint to reopen it existed
          // and nothing called it.
          <CourseButton
            label="Unpublish to edit"
            variant="review"
            onPress={() => actions.unpublish.mutate({}, {onError: (e: unknown) => setError(errorText(e, 'Could not unpublish.'))})}
          />
        ) : null}
        <CourseButton
          label={published ? 'Published' : 'Publish'}
          disabled={published || !gate.ok}
          onPress={() => actions.publish.mutate({opened: openedList}, {onError: (e: unknown) => setError(errorText(e, 'Not ready to publish.'))})}
        />
      </View>

      {editingCourse ? (
        <View style={{backgroundColor: F.white, paddingHorizontal: S[4], paddingVertical: S[3], borderBottomWidth: 1, borderBottomColor: F.border10, gap: S[2]}}>
          <CCText style={{fontSize: T.xs, color: F.mutedText}}>
            {review.data.book.title_source === 'ai' ? 'Named by AI from the chapters.' : review.data.book.title_source === 'admin' ? 'The title typed when the course was created.' : review.data.book.title_source === 'reviewer' ? 'Renamed in review.' : 'Taken from the recording.'}
            {' A rename here is kept when the course is regenerated.'}
          </CCText>
          <TextInput
            value={courseTitle}
            onChangeText={setCourseTitle}
            accessibilityLabel="Course title"
            style={{borderWidth: 1, borderColor: 'rgba(38,35,34,0.2)', borderRadius: 8, padding: S[3], fontSize: T.sm, color: F.brownDark}}
          />
          <TextInput
            value={courseDescription}
            onChangeText={setCourseDescription}
            accessibilityLabel="Course description"
            multiline
            style={{borderWidth: 1, borderColor: 'rgba(38,35,34,0.2)', borderRadius: 8, padding: S[3], fontSize: T.sm, minHeight: 56, color: F.brownDark}}
          />
          <View style={{flexDirection: 'row', gap: S[2]}}>
            <CourseButton
              label="Save course"
              disabled={!courseTitle.trim()}
              onPress={() => {
                actions.editCourse?.mutate(
                  {title: courseTitle.trim(), description: courseDescription.trim()},
                  {onError: (e: unknown) => setError(errorText(e, 'Could not save the course.'))},
                );
                setEditingCourse(false);
              }}
            />
            <CourseButton label="Cancel" variant="review" onPress={() => setEditingCourse(false)} />
          </View>
        </View>
      ) : null}

      {!gate.ok ? (
        <View
          accessibilityRole="alert"
          accessibilityLiveRegion="polite"
          style={{backgroundColor: STATUS.warn.bg, paddingHorizontal: S[4], paddingVertical: S[3], borderBottomWidth: 1, borderBottomColor: F.border10, flexDirection: 'row', gap: S[3]}}>
          <Ionicons name="lock-closed" size={16} color={STATUS.warn.fg} style={{marginTop: 1}} />
          <View style={{flex: 1, gap: 3}}>
            <CCText style={{fontSize: T.xs, fontWeight: '700', letterSpacing: 0.4, color: STATUS.warn.fg}}>{`PUBLISH IS BLOCKED · ${failing.length} CHECK${failing.length === 1 ? '' : 'S'}`}</CCText>
            {failing.map((c, i) => (
              <CCText key={i} style={{fontSize: T.sm, color: F.brown, lineHeight: 19}}>
                <CCText style={{fontWeight: '700', color: F.brownDark}}>{`${c.label} — `}</CCText>{c.detail}
              </CCText>
            ))}
          </View>
          {/* The gate names page ids, which a reviewer cannot click. Give them
              the door rather than the room number. */}
          {upNext ? (
            <View style={{alignSelf: 'center'}}>
              <CourseButton
                label={upNext.why === 'flagged' ? 'Go to the next flag' : 'Open the next one'}
                variant="review"
                onPress={() => openPage(upNext.id)}
              />
            </View>
          ) : null}
        </View>
      ) : null}

      <View style={{flex: 1, flexDirection: narrow ? 'column' : 'row'}}>
        {rail}
        {centre}
        {right}
      </View>

      {dismissing ? (
        <View style={{position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, backgroundColor: 'rgba(20,20,20,0.55)', alignItems: 'center', justifyContent: 'center', padding: S[4]}}>
          <View style={[{backgroundColor: F.white, borderRadius: 16, padding: S[5], width: '100%', maxWidth: 520, gap: S[3]}, CARD_SHADOW]}>
            <CCText accessibilityRole="header" style={{fontSize: T.lg, fontWeight: '700', color: F.brownDark}}>Mark as fine</CCText>
            <CCText style={{fontSize: T.sm, color: F.brown, lineHeight: 20}}>This closes the flag without changing anything. It will be listed by name at publish. Say why.</CCText>
            <CCText style={{fontSize: T.xs, fontWeight: '700', letterSpacing: 0.5, color: F.mutedText}}>REASON</CCText>
            <TextInput
              value={reason}
              onChangeText={setReason}
              accessibilityLabel="Reason for dismissing"
              placeholder="Checked the video — the page is correct"
              placeholderTextColor={F.mutedText}
              style={{borderWidth: 1, borderColor: 'rgba(38,35,34,0.2)', borderRadius: 8, padding: S[3], fontSize: T.sm, color: F.brownDark}}
            />
            <View style={{flexDirection: 'row', justifyContent: 'flex-end', gap: S[2], marginTop: S[1]}}>
              <CourseButton label="Cancel" variant="review" onPress={() => setDismissing(null)} />
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Confirm dismiss"
                onPress={() => {
                  if (!reason.trim()) { setError('A reason is required.'); return; }
                  actions.dismiss.mutate({flagId: dismissing.id, reason: reason.trim()}, {onError: (e: unknown) => setError(errorText(e, 'Could not dismiss.'))});
                  setDismissing(null);
                }}
                style={({pressed}) => [{backgroundColor: pressed ? F.tealDark : F.teal, borderRadius: 8, paddingHorizontal: S[4], paddingVertical: 11, minHeight: 44, justifyContent: 'center', cursor: 'pointer'} as any, motion]}>
                <CCText style={{fontSize: T.sm, fontWeight: '700', color: F.white}}>Mark as fine</CCText>
              </Pressable>
            </View>
          </View>
        </View>
      ) : null}
    </View>
  );
}
